import React, { useState, useEffect, useCallback, useRef } from "react";
import { FaTemperatureHigh, FaTint, FaWind } from "react-icons/fa";

const WeatherAirQuality = ({ className = "" }) => {
  const [weather, setWeather] = useState(null);
  const [airQuality, setAirQuality] = useState(null);
  
  // Request rate limiting
  const requestCount = useRef(0);
  const requestWindowStart = useRef(Date.now());
  const MAX_REQUESTS_PER_MINUTE = 5;
  const hasThrown = useRef(false);
  
  // Request state to prevent overlapping calls
  const isTemperatureFetching = useRef(false);
  const isHumidityFetching = useRef(false);
  const isAirQualityFetching = useRef(false);
  
  // Cache timestamps
  const lastTemperatureFetch = useRef(0);
  const lastHumidityFetch = useRef(0);
  const lastAirQualityFetch = useRef(0);

  // Check request limit before any fetch
  const checkRequestLimit = useCallback(() => {
    const now = Date.now();
    
    // Reset counter every minute
    if (now - requestWindowStart.current > 60000) {
      requestCount.current = 0;
      requestWindowStart.current = now;
    }
    
    requestCount.current++;
    
    if (requestCount.current > MAX_REQUESTS_PER_MINUTE && !hasThrown.current) {
      hasThrown.current = true;
      const error = new Error(`🔥 TOO MANY REQUESTS! Made ${requestCount.current} requests in 1 minute. Stopping all fetches.`);
      console.error(error);
      throw error;
    }
  }, []);

  // Cache validation
  const shouldFetchData = useCallback((lastFetch, cacheTime) => {
    return Date.now() - lastFetch > cacheTime;
  }, []);

  // Optimized temperature fetching with smart caching and error handling
  const fetchTemperature = useCallback(async () => {
    checkRequestLimit(); // Check request limit first
    
    // Prevent overlapping requests
    if (isTemperatureFetching.current) {
      console.log("Temperature fetch already in progress, skipping");
      return;
    }

    // Check cache validity (15 minutes)
    const CACHE_TIME = 15 * 60 * 1000;
    if (!shouldFetchData(lastTemperatureFetch.current, CACHE_TIME)) {
      console.log("Temperature data still fresh, skipping fetch");
      return;
    }

    isTemperatureFetching.current = true;

    try {
      console.log("Fetching temperature data...");
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
      
      const response = await fetch(
        "https://njs-01.optimuslab.space/lnu-footfall/floor-zone/weather",
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log("Temperature data received");

      // Multiple parsing methods to extract Tuen Mun temperature
      const htmlPattern = /Tuen Mun<\/font><\/td><td[^>]*><font[^>]*>(\d{1,2}) degrees/;
      let match = responseText.match(htmlPattern);

      if (match && match[1]) {
        const temperature = parseInt(match[1], 10);
        console.log(`Found Tuen Mun temperature in HTML: ${temperature}°C`);

        setWeather(prevWeather => ({
          temp: temperature,
          humidity: prevWeather?.humidity
        }));
        
        lastTemperatureFetch.current = Date.now();
        return;
      }

      // Fallback patterns
      const simplePattern = /Tuen Mun(?:(?!Tuen Mun).){1,100}?(\d{1,2}) degrees/s;
      match = responseText.match(simplePattern);

      if (match && match[1]) {
        const temperature = parseInt(match[1], 10);
        console.log(`Found Tuen Mun temperature using simple pattern: ${temperature}°C`);

        setWeather(prevWeather => ({
          temp: temperature,
          humidity: prevWeather?.humidity
        }));
        
        
        lastTemperatureFetch.current = Date.now();
        return;
      }

      const textPattern = /Tuen Mun(\d{1,2}) degrees/;
      match = responseText.match(textPattern);

      if (match && match[1]) {
        const temperature = parseInt(match[1], 10);
        console.log(`Found Tuen Mun temperature in plain text: ${temperature}°C`);

        setWeather(prevWeather => ({
          temp: temperature,
          humidity: prevWeather?.humidity
        }));
        
        
        lastTemperatureFetch.current = Date.now();
        return;
      }

      // Last resort - check semicolon separated
      const lines = responseText.split(';');
      for (const line of lines) {
        if (line.includes('Tuen Mun') && line.includes('degrees')) {
          const numberMatch = line.match(/(\d{1,2})\s*degrees/);
          if (numberMatch && numberMatch[1]) {
            const temperature = parseInt(numberMatch[1], 10);
            console.log(`Found Tuen Mun temperature in line: ${temperature}°C`);

            setWeather(prevWeather => ({
              temp: temperature,
              humidity: prevWeather?.humidity
            }));
            
            
            lastTemperatureFetch.current = Date.now();
            return;
          }
        }
      }

      console.log("Could not find valid Tuen Mun temperature with any pattern");
      
    } catch (error) {
      console.error("Error fetching temperature data:", error);
      
    } finally {
      isTemperatureFetching.current = false;
    }
  }, [shouldFetchData, checkRequestLimit]);

  // Optimized humidity fetching
  const fetchHumidity = useCallback(async () => {
    checkRequestLimit(); // Check request limit first
    
    if (isHumidityFetching.current) {
      console.log("Humidity fetch already in progress, skipping");
      return;
    }

    // Check cache validity (20 minutes)
    const CACHE_TIME = 20 * 60 * 1000;
    if (!shouldFetchData(lastHumidityFetch.current, CACHE_TIME)) {
      console.log("Humidity data still fresh, skipping fetch");
      return;
    }

    isHumidityFetching.current = true;

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      
      const response = await fetch(
        "https://data.weather.gov.hk/weatherAPI/opendata/weather.php?dataType=rhrread&lang=en",
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();
      const humidity = data.humidity.data[0]?.value;

      if (humidity !== undefined) {
        setWeather((prevWeather) => ({
          temp: prevWeather?.temp,
          humidity: humidity,
        }));
        
        
        lastHumidityFetch.current = Date.now();
      }
    } catch (error) {
      console.error("Error fetching humidity data:", error);
      
    } finally {
      isHumidityFetching.current = false;
    }
  }, [shouldFetchData, checkRequestLimit]);

  // Optimized air quality fetching
  const fetchAirQuality = useCallback(async () => {
    checkRequestLimit(); // Check request limit first
    
    if (isAirQualityFetching.current) {
      console.log("Air quality fetch already in progress, skipping");
      return;
    }

    // Check cache validity (30 minutes)
    const CACHE_TIME = 30 * 60 * 1000;
    if (!shouldFetchData(lastAirQualityFetch.current, CACHE_TIME)) {
      console.log("Air quality data still fresh, skipping fetch");
      return;
    }

    const fallbackData = {
      pm10: 20.7,
      pm25: 15.9,
      timestamp: new Date(),
    };

    isAirQualityFetching.current = true;

    try {
      console.log("Fetching air quality data...");
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // Longer timeout for XML parsing
      
      const response = await fetch(
        "https://njs-01.optimuslab.space/lnu-footfall/floor-zone/aqhi",
        { signal: controller.signal }
      );
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const responseText = await response.text();
      console.log("Air quality response received, first 100 chars:", responseText.substring(0, 100));

      if (responseText.trim().startsWith("<?xml") || responseText.trim().startsWith("<")) {
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(responseText, "text/xml");

          const pollutantConcentrations = xmlDoc.getElementsByTagName("PollutantConcentration");
          console.log(`Found ${pollutantConcentrations.length} PollutantConcentration elements`);

          const tuenMunEntries = [];
          for (let i = 0; i < pollutantConcentrations.length; i++) {
            const stationElement = pollutantConcentrations[i].getElementsByTagName("StationName")[0];

            if (stationElement && stationElement.textContent === "Tuen Mun") {
              const dateTimeElement = pollutantConcentrations[i].getElementsByTagName("DateTime")[0];
              const pm10Element = pollutantConcentrations[i].getElementsByTagName("PM10")[0];
              const pm25Element = pollutantConcentrations[i].getElementsByTagName("PM2.5")[0];

              if (dateTimeElement && pm10Element && pm25Element) {
                tuenMunEntries.push({
                  element: pollutantConcentrations[i],
                  dateTime: new Date(dateTimeElement.textContent),
                  pm10: parseFloat(pm10Element.textContent),
                  pm25: parseFloat(pm25Element.textContent),
                });
              }
            }
          }

          if (tuenMunEntries.length > 0) {
            console.log(`Found ${tuenMunEntries.length} Tuen Mun entries`);

            tuenMunEntries.sort((a, b) => b.dateTime - a.dateTime);
            const latestEntry = tuenMunEntries[0];
            console.log(`Using most recent entry: ${latestEntry.dateTime.toISOString()}`);

            setAirQuality({
              pm10: latestEntry.pm10,
              pm25: latestEntry.pm25,
              timestamp: latestEntry.dateTime,
            });
            
            
            lastAirQualityFetch.current = Date.now();
            return;
          } else {
            console.log("No valid Tuen Mun entries found");
          }
        } catch (xmlError) {
          console.error("Error parsing XML:", xmlError);
        }
      }

      console.warn("Could not parse response correctly. Using fallback data.");
      setAirQuality(fallbackData);
      
    } catch (error) {
      console.error("Error fetching air quality data:", error);
      
      console.log("Using fallback air quality data");
      setAirQuality(fallbackData);
    } finally {
      isAirQualityFetching.current = false;
    }
  }, [shouldFetchData, checkRequestLimit]);

  // Temperature polling - simple interval, runs once on mount
  useEffect(() => {
    fetchTemperature();
    const intervalId = setInterval(fetchTemperature, 15 * 60 * 1000); // 15 minutes
    return () => clearInterval(intervalId);
  }, []); // Empty deps - only run on mount

  // Humidity polling - staggered10safter mount
 // Humidity polling - immediate fetch on mount
useEffect(() => {
  fetchHumidity(); // Fetch immediately
  const intervalId = setInterval(fetchHumidity, 20 * 60 * 1000); // 20 minutes
  return () => clearInterval(intervalId);
}, []);

  // Air quality polling - staggered 10 minutes after mount
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      fetchAirQuality();
      const intervalId = setInterval(fetchAirQuality, 30 * 60 * 1000); // 30 minutes
      return () => clearInterval(intervalId);
    }, 10 * 60 * 1000); // 10 minute delay
    
    return () => clearTimeout(delayTimer);
  }, []); // Empty deps - only run on mount

  // Helper functions for air quality levels (unchanged)
  const getPM25Level = (value) => {
    if (value <= 12) return { level: "Good", color: "text-green-500" };
    if (value <= 35.4) return { level: "Moderate", color: "text-yellow-500" };
    if (value <= 55.4) return { level: "Unhealthy for Sensitive", color: "text-orange-600" };
    if (value <= 150.4) return { level: "Unhealthy", color: "text-red-500" };
    if (value <= 250.4) return { level: "Very Unhealthy", color: "text-purple-600" };
    return { level: "Hazardous", color: "text-red-800" };
  };

  const getPM10Level = (value) => {
    if (value <= 54) return { level: "Good", color: "text-green-500" };
    if (value <= 154) return { level: "Moderate", color: "text-yellow-500" };
    if (value <= 254) return { level: "Unhealthy for Sensitive", color: "text-orange-600" };
    if (value <= 354) return { level: "Unhealthy", color: "text-red-500" };
    if (value <= 424) return { level: "Very Unhealthy", color: "text-purple-600" };
    return { level: "Hazardous", color: "text-red-800" };
  };

  return (
    <div className={`flex flex-wrap items-center gap-4 ${className}`}>
      {/* Weather Information */}
      <div className="py-2 px-4">
  <div className="flex items-center space-x-4 text-sm sm:text-lg">
    <div className="flex items-center">
      <FaTemperatureHigh className="mr-2 text-amber-400" />
      <span className="font-medium">{weather?.temp ?? '--'}°C</span>
    </div>
    <div className="flex items-center">
      <FaTint className="mr-2 text-blue-300" />
      <span className="font-medium">{weather?.humidity ?? '--'}%</span>
    </div>
  </div>
</div>

      {/* Air Quality Information - Commented out as in original */}
      {/* {airQuality && (
        <div className="bg-white py-2 px-4 rounded-xl border border-[#d4d4d4]">
          <div className="flex items-center space-x-4 text-sm sm:text-lg">
            <div className="flex items-center">
              <FaWind className="mr-2 text-gray-600" />
              <div className="flex items-center">
                <span className="font-semibold mr-1">PM2.5:</span>
                <span className={`font-bold ${getPM25Level(airQuality.pm25).color}`}>
                  {airQuality.pm25.toFixed(1)}
                </span>
              </div>
            </div>

            <div className="flex items-center">
              <FaWind className="mr-2 text-gray-400" />
              <div className="flex items-center">
                <span className="mr-1">PM10:</span>
                <span className={`${getPM10Level(airQuality.pm10).color}`}>
                  {airQuality.pm10.toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )} */}
    </div>
  );
};

export default WeatherAirQuality;